import {
  createMoonId,
  isSavedMoonDesign,
  type MoonDesignDraft,
  type SavedMoonDesign,
} from './MoonDesign';

const STORAGE_KEY = 'moon-observation-game:moon-collection:v1';
export const MAX_ORBIT_MOONS = 6;

export class MoonCollection {
  private moons: SavedMoonDesign[] = [];

  constructor(private readonly storage: Storage | null = typeof localStorage === 'undefined' ? null : localStorage) {
    this.load();
  }

  getAll(): readonly SavedMoonDesign[] {
    return this.moons;
  }

  add(draft: MoonDesignDraft): SavedMoonDesign | null {
    if (this.moons.length >= MAX_ORBIT_MOONS) return null;
    const now = Date.now();
    const usedNames = new Set(this.moons.map((moon) => moon.name));
    let nameIndex = 1;
    while (usedNames.has(`나의 달 ${nameIndex}`)) nameIndex += 1;
    const moon: SavedMoonDesign = {
      ...draft,
      version: 1,
      id: createMoonId(now),
      createdAt: now,
      name: `나의 달 ${nameIndex}`,
      craters: draft.craters.map((crater) => ({
        ...crater,
        normal: { ...crater.normal },
      })),
    };
    this.moons = [...this.moons, moon];
    this.save();
    return moon;
  }

  remove(id: string): boolean {
    const next = this.moons.filter((moon) => moon.id !== id);
    if (next.length === this.moons.length) return false;
    this.moons = next;
    this.save();
    return true;
  }

  isFull(): boolean {
    return this.moons.length >= MAX_ORBIT_MOONS;
  }

  clear(): void {
    this.moons = [];
    this.save();
  }

  private load(): void {
    if (!this.storage) return;
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return;
      const value: unknown = JSON.parse(raw);
      if (!Array.isArray(value)) return;
      this.moons = value.filter(isSavedMoonDesign).slice(0, MAX_ORBIT_MOONS);
    } catch {
      this.moons = [];
    }
  }

  private save(): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(this.moons));
    } catch {
      // The creative loop remains playable when storage is unavailable or full.
    }
  }
}
